// @ts-check
// Endereço com o CEP primeiro (CEP na família, pacote 1.12.0, contrato 07, seção 9.9; consulta do contrato 08 de
// navegação, seção 5): o CEP completo consulta o Worker (BrasilAPI e ViaCEP) e preenche rua, bairro (no complemento),
// cidade e UF. usePostcodeLookup guarda a consulta, a última resposta aplicada e o estado, e serve ao editor de endereço
// da célula, ao editor só do CEP e ao formulário de criação, no molde do controle de telefone.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { lookupPostcode } from '../../data/navigation.js';
import { rememberPostcodeStatus } from '../../data/postcode-statuses.js';
import {
  DEFAULT_ADDRESS_COUNTRY, applyPostcodeAddress, maskPostcode, readPostcodeDigits, toCountryLabel,
} from '../../../../shared/address/postcode-autofill.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/address/postcode-autofill.js').AddressDraft} AddressDraft
 * @typedef {import('../../../../shared/address/postcode-autofill.js').PostcodeAddress} PostcodeAddress
 * @typedef {'idle' | 'searching' | 'filled' | 'notFound' | 'unavailable'} PostcodeLookupStatus
 */

const STATUS_KEYS = Object.freeze({
  searching: 'navigationMobile.address.postcodeSearching',
  filled: 'navigationMobile.address.postcodeFilled',
  notFound: 'navigationMobile.address.postcodeNotFound',
  unavailable: 'navigationMobile.address.postcodeUnavailable',
});

/** Texto da linha de estado da consulta, ou null parado. @param {PostcodeLookupStatus} status */
export const postcodeStatusText = (status) => (status === 'idle' ? null : t(STATUS_KEYS[status]));

/** Variante visual da linha de estado. @param {PostcodeLookupStatus} status */
export const postcodeStatusVariant = (status) => (status === 'filled' ? 'success' : status === 'searching' ? undefined : 'error');

/**
 * Consulta do CEP sobre um rascunho de endereço: digitar o CEP aplica a máscara e, com 8 dígitos novos, consulta e
 * preenche o rascunho (applyPostcodeAddress, que não apaga o número digitado na rua).
 * @param {AddressDraft} draft
 * @param {(draft: AddressDraft) => void} setDraft
 * @param {{ countryAsLabel?: boolean, initialDigits?: string | null }} [options]
 *   countryAsLabel: o rascunho guarda o país como texto da tela ("Brasil"); initialDigits: CEP já consultado (o gravado)
 */
export function usePostcodeLookup(draft, setDraft, { countryAsLabel = false, initialDigits } = {}) {
  const [status, setStatus] = useState(/** @type {PostcodeLookupStatus} */ ('idle'));
  const [lastAddress, setLastAddress] = useState(/** @type {PostcodeAddress | null} */ (null));
  const previousRef = useRef(/** @type {PostcodeAddress | null} */ (null));
  const lookedUpRef = useRef(initialDigits === undefined ? readPostcodeDigits(draft.addressPostcode) : initialDigits);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const pendingRef = useRef(/** @type {Promise<PostcodeAddress | null> | null} */ (null));

  /** @param {AddressDraft} next */
  const update = (next) => {
    draftRef.current = next;
    setDraft(next);
  };

  /** @param {string} digits */
  const search = (digits) => {
    lookedUpRef.current = digits;
    setStatus('searching');
    const request = lookupPostcode(digits).then(
      (address) => {
        rememberPostcodeStatus(digits, 'FOUND');
        if (lookedUpRef.current !== digits) return null;
        const next = applyPostcodeAddress(draftRef.current, address, previousRef.current);
        previousRef.current = address;
        update(countryAsLabel ? { ...next, addressCountry: toCountryLabel(next.addressCountry) } : next);
        setLastAddress(address);
        setStatus('filled');
        return address;
      },
      (/** @type {any} */ error) => {
        if (error?.statusCode === 404) rememberPostcodeStatus(digits, 'NOT_FOUND');
        if (lookedUpRef.current !== digits) return null;
        setStatus(error?.statusCode === 404 ? 'notFound' : 'unavailable');
        return null;
      },
    );
    pendingRef.current = request;
    return request;
  };

  /** @param {string} text texto digitado no campo do CEP */
  const changePostcode = (text) => {
    const masked = maskPostcode(text);
    update({ ...draftRef.current, addressPostcode: masked });
    const digits = readPostcodeDigits(masked);
    if (digits && digits !== lookedUpRef.current) search(digits);
    if (!digits) {
      lookedUpRef.current = null;
      setStatus('idle');
      setLastAddress(null);
    }
  };

  return {
    status,
    statusText: postcodeStatusText(status),
    statusVariant: postcodeStatusVariant(status),
    lastAddress,
    draftRef,
    changePostcode,
    /** Consulta ainda em andamento (para completar o endereço depois de gravar só o CEP), ou null. */
    pendingLookup: () => (status === 'searching' ? pendingRef.current : null),
  };
}

/**
 * Endereço do formulário de criação: CEP primeiro, mascarado, com o estado da consulta; depois logradouro e número,
 * bairro e complemento, e cidade e UF lado a lado. O país fica oculto (Brasil).
 * @param {{ value: AddressDraft | null | undefined, onChange: (value: AddressDraft) => void, autoFocus?: boolean, error?: string | null }} props
 */
export function PostcodeAddressControl({ value, onChange, autoFocus = false, error = null }) {
  const draft = { addressCountry: DEFAULT_ADDRESS_COUNTRY, ...(value ?? {}) };
  const lookup = usePostcodeLookup(draft, onChange);
  const focus = (/** @type {HTMLInputElement | null} */ element) => {
    if (autoFocus && element && !element.dataset.focused) {
      element.dataset.focused = 'true';
      requestAnimationFrame(() => element.focus());
    }
  };
  const text = (/** @type {keyof AddressDraft} */ name, /** @type {string} */ placeholder, /** @type {string} */ className) => html`<input
    class=${`text-input ${className}`}
    value=${draft[name] ?? ''}
    placeholder=${placeholder}
    aria-label=${placeholder}
    onInput=${(/** @type {any} */ event) => onChange({ ...lookup.draftRef.current, [name]: event.currentTarget.value })}
  />`;
  return html`<div class="postcode-address-control">
    <input
      class="text-input postcode-address-control-postcode"
      ref=${focus}
      inputmode="numeric"
      autocomplete="postal-code"
      maxlength="9"
      placeholder=${t('familyPostcode.editor.placeholder')}
      aria-label=${t('familyPostcode.subFields.addressPostcode')}
      aria-invalid=${error ? 'true' : undefined}
      value=${draft.addressPostcode ?? ''}
      onInput=${(/** @type {any} */ event) => lookup.changePostcode(event.currentTarget.value)}
    />
    ${error
      ? html`<p class="address-postcode-status" role="alert" data-variant="error">${error}</p>`
      : lookup.statusText
        ? html`<p class="address-postcode-status" role="status" data-variant=${lookup.statusVariant}>${lookup.statusText}</p>`
        : null}
    ${text('addressStreet1', t('familyPostcode.form.street'), 'postcode-address-control-street')}
    ${text('addressStreet2', t('familyPostcode.form.neighborhood'), 'postcode-address-control-neighborhood')}
    <div class="postcode-address-control-city-row">
      ${text('addressCity', t('familyPostcode.form.city'), 'postcode-address-control-city')}
      ${text('addressState', t('familyPostcode.form.state'), 'postcode-address-control-state')}
    </div>
  </div>`;
}
